import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/catalyst/Badge";
import { Switch } from "@/components/catalyst/Switch";
import { TableBody, TableCell, TableRow } from "@/components/catalyst/Table";
import { InfoTooltip } from "@/components/InfoTooltip";
import { orpc } from "@/orpc/client";
import { cn } from "@/util/string";
import { AppPartnersDialogLayout } from "./AppPartnersDialogLayout";

interface AppPartnersDialogProps {
	appId: string;
	isOpen: boolean;
	onClose: () => void;
}

export function AppPartnersDialog({
	appId,
	isOpen,
	onClose,
}: AppPartnersDialogProps) {
	const queryClient = useQueryClient();

	const { data: partners } = useSuspenseQuery(
		orpc.partners.list.queryOptions({
			input: { appId },
		}),
	);

	const updateMutation = useMutation(
		orpc.partners.update.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: orpc.partners.list.key(),
				});
				toast.success("Partnership updated");
			},
			onError: (error) => {
				toast.error(error.message);
			},
		}),
	);

	return (
		<AppPartnersDialogLayout isOpen={isOpen} onClose={onClose}>
			<TableBody>
				{partners.length === 0 ? (
					<TableRow>
						<TableCell colSpan={4} className="text-center text-base-content/60">
							No partners found for this app. Use the global Partners page to
							create partners.
						</TableCell>
					</TableRow>
				) : (
					partners.map((partner) => (
						<TableRow key={partner.id}>
							<TableCell className="font-medium flex items-center gap-1">
								<div className={cn({ "opacity-30": !partner.active })}>
									{partner.name}
								</div>
								{(!partner.stripePayoutsEnabled ||
									!partner.stripeAccountId) && (
									<InfoTooltip
										content="This partner is not connected to Stripe and will not receive payouts."
										severity="warning"
									/>
								)}
							</TableCell>
							<TableCell>
								{partner.active ? (
									<Badge color="green">Active</Badge>
								) : (
									<Badge color="zinc">Inactive</Badge>
								)}
							</TableCell>
							<TableCell>
								<Switch
									checked={!!partner.active}
									onChange={(checked) => {
										updateMutation.mutate({
											id: partner.id,
											active: checked,
										});
									}}
								/>
							</TableCell>
						</TableRow>
					))
				)}
			</TableBody>
		</AppPartnersDialogLayout>
	);
}
